<template>
  <div class="customer__detail is-active">
    <Navi
      :id="customer.id"
      :onlyEdit="isFromReservationList"
      :isSP="isSP"
      :busy="busy"
      :maintenance="GNAVI_MAINTENANCE"
      @edit="editCustomer"
      @delete="deleteCustomer"
      @integrate="customerSelectModal = true"
      @imageUploaded="getCustomerImage"
    />
    <VirtualScroller
      class="customer__book"
      v-infinite-scroll="getVisitHistory"
      infinite-scroll-disabled="historyInfinityDisabled"
      infinite-scroll-distance="20"
      :buffer="buffer"
      :items="history.list"
      :heightCache="heights"
      min-item-height="36"
    >
      <div slot="before-container" ref="beforeContainer">
        <ImageMemo v-if="!GNAVI_MAINTENANCE && allImageMemos.length" @select="currentImageId = $event" />
        <dl class="cstm-note">
          <dt>ふりがな</dt>
          <dd class="note--ruby">
            {{ customer.lastNameKana }} {{ customer.firstNameKana }}
            <VisitCount
              v-if="hasKana"
              :visitCount="customer.visitCount"
              :visitCountOther="customer.visitCountOther"
              hiddenZero
            />
          </dd>
          <dt>お名前</dt>
          <dd class="note--ruby">
            {{ customer.lastName }} {{ customer.firstName }}
            <VisitCount
              v-if="!hasKana && hasName"
              :visitCount="customer.visitCount"
              :visitCountOther="customer.visitCountOther"
              hiddenZero
            />
          </dd>
          <dt>電話番号1</dt>
          <dd class="customer-tels">
            <div class="note--ruby">
              {{ customer.tel1 }}
              <VisitCount
                v-if="!hasKana && !hasName && customer.tel1"
                :visitCount="customer.visitCount"
                :visitCountOther="customer.visitCountOther"
                hiddenZero
              />
            </div>
            <div v-if="customer.tel1DuplicationFlag" class="tel-dup">※この番号に紐づく顧客情報が複数あります</div>
          </dd>
          <dt>電話番号2</dt>
          <dd class="customer-tels">
            <div class="note--ruby">
              {{ customer.tel2 }}
              <VisitCount
                v-if="!hasKana && !hasName && !customer.tel1 && customer.tel2"
                :visitCount="customer.visitCount"
                :visitCountOther="customer.visitCountOther"
                hiddenZero
              />
            </div>
            <div v-if="customer.tel2DuplicationFlag" class="tel-dup">※この番号に紐づく顧客情報が複数あります</div>
          </dd>
          <dt>メール1</dt>
          <dd>{{ customer.mail1 }}</dd>
          <dt>メール2</dt>
          <dd>{{ customer.mail2 }}</dd>
        </dl>
        <dl class="cstm-note">
          <dt>会社名</dt>
          <dd>{{ customer.officeName }}</dd>
          <dt>部署</dt>
          <dd>{{ customer.department }}</dd>
          <dt>役職名</dt>
          <dd>{{ customer.post }}</dd>
          <dt>会社郵便番号</dt>
          <dd>{{ formatZip(customer.officeZipCode) }}</dd>
          <dt>会社住所</dt>
          <dd>{{ customer.officeAddress }}</dd>
          <dt>会社電話番号</dt>
          <dd>{{ customer.officeTel }}</dd>
          <dt>領収書宛名</dt>
          <dd>{{ customer.receiptAddress }}</dd>
          <dt>勤務先メモ</dt>
          <dd>{{ customer.officeRemarks }}</dd>
        </dl>
        <dl class="cstm-note">
          <dt>自宅郵便番号</dt>
          <dd>{{ formatZip(customer.zipCode) }}</dd>
          <dt>自宅住所</dt>
          <dd>{{ customer.address }}</dd>
        </dl>
        <dl class="cstm-note">
          <template v-if="!!syncMemberType">
            <dt>会員情報</dt>
            <dd>{{ syncMemberType }}</dd>
          </template>
          <dt>お客様特徴</dt>
          <dd class="customer-tags">
            <TagLabels v-if="attributes" :tags="attributes" />
            <TagLabels v-if="customer.otherAttributes" :tags="customer.otherAttributes" />
          </dd>
          <dt>性別</dt>
          <dd>{{ customer.gender ? genderMap[customer.gender] : '' }}</dd>
          <dt>誕生日</dt>
          <dd>{{ formatDate(customer.birthday) }}</dd>
          <dt>結婚記念日</dt>
          <dd>{{ formatDate(customer.wedding) }}</dd>
          <dt>その他記念日</dt>
          <dd>
            <ul>
              <li v-for="(anniversary, key) in customer.otherAnniversaries" :key="key">
                <span>{{ anniversary.name }}</span>
                <span>{{ formatDate(anniversary.date) }}</span>
              </li>
            </ul>
          </dd>
          <dt>喫煙情報</dt>
          <dd>{{ customer.smoking ? smokingMap[customer.smoking] : '' }}</dd>
          <dt>アレルギー</dt>
          <dd>{{ customer.allergies }}</dd>
          <dt>好きな料理</dt>
          <dd>{{ customer.favoriteFood }}</dd>
          <dt>苦手な料理</dt>
          <dd>{{ customer.notFavoriteFood }}</dd>
          <dt>お客様メモ</dt>
          <dd>{{ customer.customerRemarks }}</dd>
        </dl>
        <dl class="cstm-note">
          <dt>Twitter</dt>
          <dd>{{ customer.twitter }}</dd>
          <dt>Facebook</dt>
          <dd>{{ customer.facebook }}</dd>
          <dt>DM可否</dt>
          <dd>{{ customer.id ? ['指定なし', '受け取る', '受け取らない'][customer.directMail] : '' }}</dd>
        </dl>
        <dl v-if="hasOptions" class="cstm-note">
          <template v-for="{ id, label, type, list } in currentStore.optionsSchema.customer">
            <dt :key="`label-${id}`">{{ label }}</dt>
            <dd v-if="type === 1" :key="`value-${id}`">{{ customer.options && customer.options[id] }}</dd>
            <dd v-if="type === 5" :key="`value-${id}`">
              <ul class="option-check-list">
                <li v-for="(label, i) in optionCheckLabels(list, id)" :key="i">{{ label }}</li>
              </ul>
            </dd>
          </template>
        </dl>
        <dl class="cstm-note" :class="{ 'dl-bottom': !hasHistoryOptions }">
          <dt>備考</dt>
          <dd class="remarks">{{ customer.remarks }}</dd>
          <dt>お客様関係者</dt>
          <dd class="customer-relations">
            <ul>
              <li v-for="{ id, name } in customer.customerRelations" :key="id">
                <a href="#" @click.prevent="() => onClickRelation(id)">{{ name }} <small>様</small></a>
              </li>
            </ul>
          </dd>
          <dt v-if="!hasHistoryOptions" :class="{ history: history.list.length }">来店履歴</dt>
        </dl>
        <dl v-if="hasHistoryOptions" class="cstm-note dl-bottom">
          <dt>来店履歴</dt>
          <dd v-if="customer.id">
            <Button
              small
              class="add-history"
              :disabled="isFromCustomer || GNAVI_MAINTENANCE"
              @click="onClickVisitHistory(null)"
            >
              <AddIcon slot="icon" />履歴追加
            </Button>
          </dd>
        </dl>
      </div>
      <CellMeasurer slot-scope="{ item, itemIndex, itemKey }" :key="itemKey" :heights="heights" :index="itemIndex">
        <div class="visit-history">
          <a
            class="trigger"
            :class="{
              toggle: !hasHistoryOptions,
              opend: !!historyToggles[itemIndex],
            }"
            @click="onClickVisitHistory(itemIndex)"
          >
            <TextLabel v-if="item.storeId !== currentStore.id">他</TextLabel>
            {{ visitDate(item.date) }} {{ item.time }}
          </a>
          <span class="status" :class="{ warn: item.visitStatus === 0 }">{{ statusLabel(item) }}</span>
          <VisitHistory
            v-if="!hasHistoryOptions && !!historyToggles[itemIndex]"
            :history="item"
            @showEatInfoModal="eatInfoModal = $event"
          />
        </div>
      </CellMeasurer>
      <div v-if="history.busy" slot="after-container">
        <LoadingIndicator small />
      </div>
    </VirtualScroller>
    <LoadingIndicator v-if="detail.busy" />
    <Lightbox
      v-if="currentImageId"
      :slideImageId="currentImageId"
      @select="currentImageId = $event"
      @close="currentImageId = null"
    />
    <CustomerInfoModal v-if="showCustomerInfo" @close="showCustomerInfo = false" />
    <EatInfoModal v-if="eatInfoModal" :reserveId="eatInfoModal" @close="eatInfoModal = null" />
    <VisitHistoryModal
      v-if="visitHistoryModal"
      :index="visitHistoryIndex"
      @change="reloadHistory"
      @close="visitHistoryModal = false"
    />
    <CustomerSelectModal
      v-if="customerSelectModal"
      title="統合する顧客を選択"
      :ignoreIds="[customer.id]"
      @select="confirmIntegration"
      @hide="customerSelectModal = false"
    />
    <IntegrationConfirmModal
      v-if="integrationConfirmModal"
      @executeIntegration="executeIntegration"
      @hide="hideIntegrationConfirmModal"
    />
  </div>
</template>

<script>
import { intersection } from 'lodash'
import InfiniteScroll from 'vue-infinite-scroll'
import { mapState, mapGetters } from 'vuex'
import { format as formatZip } from '@/utils/zipCode'
import { formatDate, splitDate, toHourClock } from '@/utils/date'
import CustomerInfoModal from '@/components/organisms/CustomerInfoModal'
import EatInfoModal from '@/components/organisms/EatInfoModal'
import VisitHistoryModal from '@/components/organisms/VisitHistoryModal'
import Lightbox from '@/components/molecules/Lightbox'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import ImageMemo from '@/components/organisms/ImageMemo'
import Dialog from '@/components/molecules/ModalDialog'
import TagLabels from '@/components/molecules/TagLabels'
import VirtualScroller, { CellMeasurer } from '@/components/molecules/VirtualScroller'
import VisitCount from '@/components/molecules/VisitCount'
import Button from '@/components/atoms/Button'
import TextLabel from '@/components/atoms/TextLabel'
import AddIcon from '@/components/icons/add'
import { attributesMap } from '@/resources/customer/attributesMap'
import { genderMap } from '@/resources/customer/genderMap'
import { smokingMap } from '@/resources/customer/smokingMap'
import Navi from '../Navi'
import VisitHistory from './VisitHistory'
import CustomerSelectModal from '../modal/CustomerSelectModal'
import IntegrationConfirmModal from '../modal/IntegrationConfirmModal'

/**
 * 顧客詳細
 */
export default {
  name: 'CustomerDetail',
  components: {
    Button,
    TextLabel,
    Lightbox,
    LoadingIndicator,
    VirtualScroller,
    VisitCount,
    CellMeasurer,
    ImageMemo,
    TagLabels,
    Navi,
    VisitHistory,
    CustomerInfoModal,
    CustomerSelectModal,
    EatInfoModal,
    IntegrationConfirmModal,
    VisitHistoryModal,
    AddIcon,
  },
  directives: {
    InfiniteScroll,
  },
  props: {
    isFromReservationList: Boolean,
    heights: Array,
    historyToggles: Array,
    isScrollTop: Boolean,
  },
  data() {
    return {
      buffer: 0,
      currentImageId: null,
      showCustomerInfo: false,
      eatInfoModal: null,
      visitHistoryModal: false,
      visitHistoryIndex: null,
      customerSelectModal: false,
      integrationConfirmModal: false,
      GNAVI_MAINTENANCE,
    }
  },
  computed: {
    genderMap: () => genderMap,
    smokingMap: () => smokingMap,
    ...mapState({
      isSP: state => state.app.isSP,
      busy: state => state.imageMemos.busy,
      currentStore: state => state.stores.current.store,
      detail: state => state.customers.edit,
      customer: state => state.customers.edit.customer,
      history: state => state.customers.detail.visits,
      syncMemberTypes: state => state.customers.syncMemberTypes,
    }),
    ...mapGetters({
      allImageMemos: 'imageMemos/all',
    }),
    hasKana() {
      const { lastNameKana, firstNameKana } = this.customer
      return lastNameKana || firstNameKana
    },
    hasName() {
      const { lastName, firstName } = this.customer
      return lastName || firstName
    },
    historyInfinityDisabled() {
      return this.history.busy || !this.history.next
    },
    hasOptions() {
      return !!(this.currentStore.optionsSchema.customer && this.currentStore.optionsSchema.customer.length)
    },
    hasHistoryOptions() {
      return !!(this.currentStore.optionsSchema.visitHistory && this.currentStore.optionsSchema.visitHistory.length)
    },
    isFromCustomer() {
      return document.referrer.includes('customer/init')
    },
    attributes() {
      return this.customer.attributes.map(id => {
        const { label, type } = attributesMap[id]
        return { id, name: label, color: { normal: '#ff9300', bad: '#dd2c00' }[type] }
      })
    },
    syncMemberType() {
      return this.syncMemberTypes.byId[this.customer.syncMemberType]
    },
  },
  updated() {
    this.buffer = this.$refs.beforeContainer.clientHeight
    if (this.isScrollTop) {
      const el = document.getElementsByClassName('customer__book')[0]
      el.scrollTo(0, 0)
      this.$emit('disabledScrollTop')
    }
  },
  methods: {
    formatZip,
    formatDate(date) {
      return formatDate(date, 'YYYY 年 MM 月 DD 日')
    },
    visitDate(date) {
      const { year, month, day } = splitDate(date)
      return year ? formatDate(date, 'YYYY/M/D (dd)') : `${month}/${day}`
    },
    statusLabel({ visitStatus: status, date, time }) {
      if (status === 0) return '（無断キャンセル）'
      if (status === 1) return '（事前キャンセル）'
      if (status === 3 || status === 4) {
        const now = toHourClock(new Date())
        if (now.date < date || (now.date === date && now.time < time.split('〜')[0])) {
          return '（来店前）'
        }
      }
      return null
    },
    editCustomer() {
      this.$emit('editCustomer')
    },
    deleteCustomer() {
      this.$emit('deleteCustomer')
    },
    getCustomerImage() {
      this.$emit('getCustomerImage', this.customer.id)
    },
    onClickRelation(id) {
      this.$store.dispatch('customers/detail/get', { id })
      this.showCustomerInfo = true
    },
    getVisitHistory() {
      if (this.customer.id) {
        this.$emit('getVisitHistory', this.customer.id)
      }
    },
    onClickVisitHistory(index) {
      if (this.hasHistoryOptions) {
        this.visitHistoryIndex = index
        this.visitHistoryModal = true
      } else {
        this.$set(this.historyToggles, index, !this.historyToggles[index])
        this.$set(this.heights, index, null)
      }
    },
    async reloadHistory() {
      const { commit, dispatch } = this.$store
      commit('customers/detail/visits/clear')
      dispatch('customers/detail/visits/get', this.customer.id)
      const customer = await dispatch('customers/detail/get', { id: this.customer.id })
      commit('customers/detail/set')
      if (customer) {
        const { id, visitCount, lastVisitDate } = customer
        const supplementary = `最終来店：${lastVisitDate ? formatDate(lastVisitDate) : ''}`
        commit('customer/updateCustomerListById', { id, visit_count: visitCount, supplementary })
      }
    },

    hideIntegrationConfirmModal() {
      this.integrationConfirmModal = false
      this.customerSelectModal = true
    },
    async confirmIntegration({ id: distId }) {
      if (await this.$store.dispatch('customers/integration/confirm', { id: this.detail.customer.id, distId })) {
        this.customerSelectModal = false
        this.integrationConfirmModal = true
      }
    },
    async executeIntegration({ id, distId }) {
      if (await this.$store.dispatch('customers/integration/execute', { id, distId })) {
        this.integrationConfirmModal = false
        if (
          await Dialog.show({
            title: `統合完了しました`,
            message: `顧客データの統合が完了しました。`,
            okText: 'OK',
          })
        ) {
          this.$emit('integrated', id)
        }
      }
    },
    optionCheckLabels(list, id) {
      const options = this.customer.options[id]
      if (!options) return false
      return intersection(Object.keys(list), options.split(',')).map(key => list[key])
    },
  },
}
</script>

<style lang="postcss" scoped>
.customer__detail {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  .responsive & {
    @media screen and (--sp-modal) {
      background-color: var(--white);

      .customer__header {
        justify-content: center;
      }
    }
  }
}

.customer__book {
  box-sizing: border-box;
  flex: 1;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  overflow-y: scroll;
  padding: 0 32px 40px;

  .responsive & {
    max-width: 100vw;

    @media screen and (--sp-modal) {
      margin-bottom: 16px;
      padding: 0 14px;
    }
  }
}

.image-memo-body + .cstm-note {
  border-top: 1px solid var(--border-color);
}

.customer__book .cstm-note {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
  padding-top: 20px;
  position: relative;

  .responsive & {
    @media screen and (--sp-modal) {
      display: block;

      >>> dt {
        height: auto;
        line-height: normal;
        margin-bottom: 8px;
        width: auto;
      }

      >>> dd {
        display: grid;
        font-size: 16px;
        grid-auto-flow: column;
        width: 100%;
      }

      .visit-count {
        justify-content: flex-end;
      }

      .customer-tags {
        display: block;
      }

      .customer-tels {
        display: block;
        >>> .note--ruby {
          align-items: baseline;
          justify-content: space-between;
        }
      }

      .customer-relations li + li {
        margin-top: 8px;
      }

      .option-check-list {
        grid-template-columns: auto;
      }
    }
  }

  & + .cstm-note {
    border-top: 1px solid var(--border-color);
  }

  &:first-of-type {
    margin-top: 0;
  }

  >>> dt {
    color: var(--gray);
    font-size: 13px;
    font-weight: bold;
    height: 48px;
    line-height: 1.5;
    width: 88px;

    &:last-of-type {
      height: auto;
    }
  }

  >>> dd {
    align-items: center;
    box-sizing: border-box;
    font-size: 18px;
    margin-bottom: 20px;
    padding-left: 24px;
    width: calc(100% - 88px);

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  >>> .note--ruby {
    display: flex;
  }
}

.tel-dup {
  color: var(--pure-red);
  font-size: 12px;
  font-weight: 600;
  margin-top: 3px;
}

.dl-bottom {
  padding-bottom: 20px;
  .responsive & {
    @media screen and (--sp-modal) {
      padding-bottom: 0;
    }
  }
}

.history {
  bottom: -28px;
  position: absolute;

  .responsive & {
    @media screen and (--sp-modal) {
      bottom: 0;
      margin-top: 20px;
      position: static;
    }
  }
}

.option-check-list {
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 50% 50%;

  label {
    cursor: pointer;
  }
}

.remarks {
  white-space: pre-wrap;
}

.visit-history {
  align-items: center;
  display: grid;
  font-size: 18px;
  grid-gap: 10px 4px;
  grid-template-columns: max-content 1fr;
  padding-bottom: 8px;
  padding-left: 96px;
  position: relative;

  .responsive & {
    @media screen and (--sp-size) {
      display: block;
      font-size: 16px;
      padding-left: 16px;
    }
  }
}

.trigger {
  align-items: center;
  cursor: pointer;
  display: inline-grid;
  grid-auto-flow: column;
  grid-gap: 8px;
  position: relative;

  .responsive & {
    @media screen and (max-width: 530px) {
      display: block;
    }
  }
}

.toggle {
  padding: 0 12px 0 22px;

  &::before {
    border: 8px solid transparent;
    border-left: 8px solid var(--very-dark-gray-2);
    box-sizing: border-box;
    content: '';
    height: 8px;
    left: 0;
    position: absolute;
    top: 6px;
    width: 8px;
  }
  .responsive & {
    @media screen and (--sp-modal) {
      &::before {
        top: 3px;
      }
    }
  }

  &.opend {
    &::before {
      left: -2px;
      top: 10px;
      transform: rotate(90deg);
    }
  }
}

.status {
  font-size: 15px;
  font-weight: 600;
  line-height: 1;

  .responsive & {
    @media screen and (--sp-modal) {
      font-size: 13px;
    }
  }
}

.warn {
  color: var(--pure-red);
}

.add-history {
  width: 128px;
}
</style>
